/**
 * Note (Derek, 2025-01-14, REPL-20840): This component was refactored to simplify
 * dimension selection for images. Key changes:
 * - Removed useOverridableState/useDebouncedCallback in favor of direct value handling
 * - Added useComputedElementDimensions to display actual rendered dimensions for images
 *   that have dimensions set via HTML attributes (width/height) rather than CSS classes
 * - Added excludeOptions prop to allow hiding certain modes (e.g., hiding "Fill" for containers)
 * - Split rendering into two modes: Combobox for Hug/Fill, LengthInputSelector for Fixed
 */
import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";

import React from "react";

import DimensionFill from "@svg/modifiers/direction/dimension-fill";
import DimensionFixed from "@svg/modifiers/direction/dimension-fixed";
import DimensionHug from "@svg/modifiers/direction/dimension-hug";

import {
  ComboboxContent,
  ComboboxHeader,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTitle,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import twMerge from "@replo/design-system/utils/twMerge";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { ChevronsUpDown } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

export type DimensionMode = "auto" | "full" | "fixed";

interface DimensionSelectorProps {
  type: "width" | "height";
  value: number | "auto" | "full" | null;
  onSelect: (value: number | "auto" | "full" | null) => void;
  /** Options to exclude from the dropdown (e.g., ["full"] to hide Fill option) */
  excludeOptions?: DimensionMode[];
  /** Computed element dimension (width or height in px) to display as hint */
  computedDimension?: number | null;
}

const DimensionSelector: React.FC<DimensionSelectorProps> = ({
  type,
  value,
  onSelect,
  excludeOptions = [],
  computedDimension = null,
}) => {
  const computedDimensionString =
    computedDimension !== null ? `${Math.round(computedDimension)}px` : "";

  const isNonFixedMode = value === "auto" || value === "full";

  const getDimensionIcon = () => {
    const hugFillClass = twMerge(
      "text-muted",
      type === "height" && "origin-center rotate-90",
      type === "width" && "size-3",
    );
    const fixedClass = twMerge(
      "text-muted",
      type === "width" && "origin-center rotate-90",
      type === "width" && "ml-0.5 size-3",
    );
    if (value === "auto") {
      return <DimensionHug className={hugFillClass} />;
    }
    if (value === "full") {
      return <DimensionFill className={hugFillClass} />;
    }
    return <DimensionFixed className={fixedClass} />;
  };

  const fixedIconClass = twMerge(
    "text-muted ml-px",
    type === "width" && "origin-center rotate-90 size-3",
  );
  const hugFillIconClass = twMerge(
    "text-muted",
    type === "height" && "origin-center rotate-90",
    type === "width" && "size-3",
  );

  const allMenuOptions = [
    // Note (Derek, 2026-01-14, REPL-20840): Only show Fixed option when we have computed dimensions
    // to avoid displaying a fake fallback value before iframe responds
    ...(computedDimensionString
      ? [
          {
            label: "Fixed",
            value: computedDimensionString,
            icon: <DimensionFixed className={fixedIconClass} />,
          },
        ]
      : []),
    {
      label: "Hug",
      value: "Hug",
      icon: <DimensionHug className={hugFillIconClass} />,
    },
    {
      label: "Fill",
      value: "Fill",
      icon: <DimensionFill className={hugFillIconClass} />,
    },
  ];

  const menuOptions = allMenuOptions.filter((option) => {
    if (option.label === "Fixed") {
      return !excludeOptions.includes("fixed");
    }
    if (option.label === "Hug") {
      return !excludeOptions.includes("auto");
    }
    if (option.label === "Fill") {
      return !excludeOptions.includes("full");
    }
    return true;
  });

  const getDisplayValue = (): string => {
    if (value === "auto") {
      return "Hug";
    }
    if (value === "full") {
      return "Fill";
    }
    if (typeof value === "number") {
      return `${value}px`;
    }
    // Note (Derek, 2025-01-14, REPL-20840): When no explicit value is set but we have computed
    // dimensions, show those as the display value instead of empty/0px. This handles
    // images with fixed dimensions via HTML attributes or inline styles.
    if (computedDimensionString) {
      return computedDimensionString;
    }
    return "";
  };

  const handleChange = (newValue: string | null) => {
    if (newValue === null || newValue === "") {
      onSelect(null);
      return;
    }

    if (newValue === "Hug" || newValue === "auto") {
      onSelect("auto");
      return;
    }
    if (newValue === "Fill" || newValue === "full") {
      onSelect("full");
      return;
    }

    const result = parseUnit(
      newValue,
      { value: 0, unit: "px" },
      "default",
      "px",
      undefined, // NOTE (Derek, 2026-01-14, REPL-20840): resetValue - unused, passing to reach later params
      ["px"], // NOTE (Derek, 2026-01-14, REPL-20840): metrics - only allow px unit
      ["auto", "full", "hug", "fill"], // NOTE (Derek, 2026-01-14, REPL-20840): allowedNonUnitValues - dimension modes
    );

    if (result.value === "auto" || result.value === "hug") {
      onSelect("auto");
      return;
    }
    if (result.value === "full" || result.value === "fill") {
      onSelect("full");
      return;
    }

    const numValue = result.value as number;
    if (numValue >= 0) {
      onSelect(Math.round(numValue));
    }
  };

  const comboboxItems: ComboboxItemWithValue[] = menuOptions.map((option) => ({
    value: option.value,
    label: option.label,
    startEnhancer: option.icon,
  }));

  const selectedItem =
    comboboxItems.find((item) => item.value === getDisplayValue()) ?? null;

  /* Note (Derek, 2026-01-14, REPL-20840): Two rendering modes based on current value:
   * - Hug/Fill: Simple Combobox dropdown showing mode + computed dimensions as hint
   * - Fixed: LengthInputSelector with editable input, drag-to-change, and mode dropdown
   */
  if (isNonFixedMode) {
    return (
      <div className="w-full overflow-hidden">
        <ComboboxRoot
          items={comboboxItems}
          value={selectedItem}
          onValueChange={(item) => handleChange(item?.value ?? null)}
          itemToStringValue={(item) => String(item.label)}
          isItemEqualToValue={(a, b) => a.value === b.value}
          filter={null}
        >
          <ComboboxTriggerButton
            className="w-full"
            startEnhancer={getDimensionIcon()}
            endEnhancer={
              <div className="flex items-center gap-1">
                {computedDimensionString && (
                  <span className="text-placeholder text-xs">
                    {computedDimensionString}
                  </span>
                )}
                <ChevronsUpDown size={12} className="text-muted" />
              </div>
            }
          >
            {getDisplayValue()}
          </ComboboxTriggerButton>
          <ComboboxContent
            sideOffset={8}
            side="bottom"
            align="start"
            UNSAFE_className="w-[120px]"
          >
            <ComboboxHeader>
              <ComboboxTitle>
                {type === "width" ? "Width" : "Height"}
              </ComboboxTitle>
            </ComboboxHeader>
            <ComboboxList>
              {(item: ComboboxItemWithValue) => (
                <ComboboxItem key={item.value} {...item} value={item} />
              )}
            </ComboboxList>
          </ComboboxContent>
        </ComboboxRoot>
      </div>
    );
  }

  return (
    <div className="w-full overflow-hidden">
      <LengthInputSelector
        field={type}
        value={getDisplayValue()}
        onChange={handleChange}
        placeholder="0px"
        metrics={["px"]}
        unitDefaults={{ value: "0", unit: "px" }}
        minValues={{ px: 0 }}
        menuOptions={menuOptions}
        allowedNonUnitValues={["auto", "full", "hug", "fill"]}
        inputClassName="w-full"
        startEnhancer={getDimensionIcon()}
        endEnhancer={() => (
          <div className="flex items-center gap-1">
            <ChevronsUpDown size={12} className="text-muted" />
          </div>
        )}
        draggingType="vertical"
      />
    </div>
  );
};

export default DimensionSelector;
